import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Beer, UserPlus, User, Heart, MapPin, Clock, CalendarCheck, Menu, X, LogIn, LogOut, Building2, Calendar, PlusCircle, MessageCircle, Globe, Users, Crown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from '../contexts/UserContext';
import PremiumBadge from '../components/PremiumBadge';
import ScrollIndicator from '../components/ScrollIndicator';
import { Search, Utensils, ChefHat, Star, Award, TrendingUp, Coffee, Zap, BookOpen } from 'lucide-react';

const CountUpNumber: React.FC<{ end: number; duration: number }> = ({ end, duration }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const steps = 50;
    const increment = end / steps;
    const timePerStep = duration / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, timePerStep);

    return () => clearInterval(timer);
  }, [end, duration, isVisible]);

  return <div ref={elementRef}>{count.toLocaleString()}</div>;
};

const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; description: string; delay: number }> = ({ 
  icon, title, description, delay 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div 
      ref={cardRef}
      className={`bg-white rounded-xl shadow-md p-6 transform transition-all duration-700 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
      }`}
    >
      <div className="flex justify-center mb-4">
        <div className="p-3 bg-orange-100 rounded-full text-orange-500">
          {icon}
        </div>
      </div>
      <h3 className="text-xl font-semibold text-center mb-2">{title}</h3>
      <p className="text-gray-600 text-center">{description}</p>
    </div>
  );
};

const TestimonialCard: React.FC<{ 
  name: string; 
  image: string; 
  text: string; 
  rating: number;
  location: string;
  index: number;
}> = ({ name, image, text, rating, location, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, index * 200);
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [index]);

  return (
    <div 
      ref={cardRef}
      className={`bg-white rounded-lg shadow-md p-6 transform transition-all duration-700 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
      }`}
    >
      <div className="flex items-center space-x-4 mb-4">
        <img
          src={image}
          alt={name}
          className="w-14 h-14 rounded-full object-cover border-2 border-orange-300"
        />
        <div>
          <h3 className="font-semibold text-gray-900">{name}</h3>
          <div className="flex text-yellow-400">
            {[...Array(rating)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-current" />
            ))}
          </div>
          <p className="text-xs text-gray-500">{location}</p>
        </div>
      </div>
      <p className="text-gray-600 italic">{text}</p>
    </div>
  );
};

const StatCard: React.FC<{ 
  icon: React.ReactNode; 
  value: number; 
  label: string; 
  duration: number;
  suffix?: string;
  prefix?: string;
}> = ({ icon, value, label, duration, suffix, prefix }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={cardRef} 
      className={`bg-white rounded-xl shadow-lg p-6 transform transition-all duration-700 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
      }`}
    >
      <div className="flex items-center justify-center mb-4">
        <div className="p-3 bg-orange-100 rounded-full text-orange-500">
          {icon}
        </div>
      </div>
      <div className="text-center">
        <div className="flex items-center justify-center text-3xl font-bold text-gray-900">
          {prefix && <span>{prefix}</span>}
          {isVisible ? <CountUpNumber end={value} duration={duration} /> : 0}
          {suffix && <span>{suffix}</span>}
        </div>
        <p className="mt-2 text-gray-600">{label}</p>
      </div>
    </div>
  );
};

const Home = () => {
  const { t } = useLanguage();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasVenue, setHasVenue] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(true);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);

  useEffect(() => {
    // Check if user just logged in or registered
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('confirmed') === 'true') {
      setShowWelcomeMessage(true);
      // Remove the parameter from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      
      // Hide welcome message after 5 seconds
      setTimeout(() => {
        setShowWelcomeMessage(false);
      }, 5000);
    }
  }, [location]);

  useEffect(() => {
    // Check if screen is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (user) {
      checkVenueOwnership();
    }
  }, [user]);

  const checkVenueOwnership = async () => {
    try {
      // Check for restaurant
      const { data: venues, error: venuesError } = await supabase
        .from('restaurants')
        .select('id')
        .eq('owner_id', user.id)
        .or('status.eq.approved,status.eq.pending')
        .limit(1);

      if (venuesError) {
        console.error('Error fetching restaurants:', venuesError);
        return;
      }

      if (!venues?.length) {
        // If no restaurant found, check for bar
        const { data: bars, error: barsError } = await supabase
          .from('bars')
          .select('id')
          .eq('owner_id', user.id)
          .or('status.eq.approved,status.eq.pending')
          .limit(1);

        if (barsError) {
          console.error('Error fetching bars:', barsError);
          return;
        }
        setHasVenue(!!bars?.length);
      } else {
        setHasVenue(true);
      }
    } catch (err) {
      console.error('Error checking venues:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      // Navigate to login page after sign out
      navigate('/login');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  // Function to render the hero navigation section
  const renderHeroNavigation = () => (
    <div className="bg-gradient-to-r from-orange-200 to-orange-300 py-4 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {isMobile ? (
          <div className="grid grid-cols-2 gap-3">
            <Link 
              to="/nearby-listings" 
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col items-center justify-center p-3 text-center"
            >
              <Users className="h-6 w-6 text-orange-500 mb-1" />
              <span className="text-sm font-medium">{t('nav.nearbyListings')}</span>
            </Link>
            
            <Link 
              to="/nearby" 
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col items-center justify-center p-3 text-center"
            >
              <MapPin className="h-6 w-6 text-orange-500 mb-1" />
              <span className="text-sm font-medium">{t('nav.nearby')}</span>
            </Link>
            
            <Link 
              to="/blind-dates" 
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col items-center justify-center p-3 text-center"
            >
              <Heart className="h-6 w-6 text-orange-500 mb-1" />
              <span className="text-sm font-medium">{t('nav.blindDates')}</span>
            </Link>
            
            {user && (
              <Link 
                to="/my-dinners" 
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col items-center justify-center p-3 text-center"
              >
                <Utensils className="h-6 w-6 text-orange-500 mb-1" />
                <span className="text-sm font-medium">{t('nav.myDinners')}</span>
              </Link>
            )}
            
            {hasVenue && (
              <Link 
                to="/my-venues" 
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col items-center justify-center p-3 text-center"
              >
                <Building2 className="h-6 w-6 text-orange-500 mb-1" />
                <span className="text-sm font-medium">{t('nav.myVenues')}</span>
              </Link>
            )}
            
            <Link 
              to="/premium" 
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col items-center justify-center p-3 text-center col-span-2"
            >
              <Award className="h-6 w-6 text-black mb-1" />
              <span className="text-sm font-medium">Premium</span>
            </Link>
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-2">
            <Link 
              to="/nearby-listings" 
              className="px-4 py-1.5 bg-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center text-sm group"
            >
              <Users className="h-4 w-4 text-orange-500 mr-2 group-hover:scale-110 transition-transform" />
              <span className="font-medium">{t('nav.nearbyListings')}</span>
            </Link>
            
            <Link 
              to="/nearby" 
              className="px-4 py-1.5 bg-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center text-sm group"
            >
              <MapPin className="h-4 w-4 text-orange-500 mr-2 group-hover:scale-110 transition-transform" />
              <span className="font-medium">{t('nav.nearby')}</span>
            </Link>
            
            <Link 
              to="/blind-dates" 
              className="px-4 py-1.5 bg-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center text-sm group"
            >
              <Heart className="h-4 w-4 text-orange-500 mr-2 group-hover:scale-110 transition-transform" />
              <span className="font-medium">{t('nav.blindDates')}</span>
            </Link>
            
            {user && (
              <Link 
                to="/my-dinners" 
                className="px-4 py-1.5 bg-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center text-sm group"
              >
                <Utensils className="h-4 w-4 text-orange-500 mr-2 group-hover:scale-110 transition-transform" />
                <span className="font-medium">{t('nav.myDinners')}</span>
              </Link>
            )}
            
            {hasVenue && (
              <>
                <Link 
                  to="/my-venues" 
                  className="px-4 py-1.5 bg-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center text-sm group"
                >
                  <Building2 className="h-4 w-4 text-orange-500 mr-2 group-hover:scale-110 transition-transform" />
                  <span className="font-medium">{t('nav.myVenues')}</span>
                </Link>
                
                {!isMobile && (
                  <>
                    <Link 
                      to="/my-events" 
                      className="px-4 py-1.5 bg-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center text-sm group"
                    >
                      <Calendar className="h-4 w-4 text-orange-500 mr-2 group-hover:scale-110 transition-transform" />
                      <span className="font-medium">{t('nav.myEvents')}</span>
                    </Link>
                    
                    <Link 
                      to="/my-reservations" 
                      className="px-4 py-1.5 bg-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center text-sm group"
                    >
                      <Calendar className="h-4 w-4 text-orange-500 mr-2 group-hover:scale-110 transition-transform" />
                      <span className="font-medium">{t('nav.myReservations')}</span>
                    </Link>
                  </>
                )}
              </>
            )}
            
            <Link 
              to="/premium" 
              className="px-4 py-1.5 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center text-sm group"
            >
              <Award className="h-4 w-4 text-black mr-2 group-hover:scale-110 transition-transform" />
              <span className="font-medium">Premium</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-8 relative">
      <ScrollIndicator />

      {/* Welcome message after login/registration */}
      {showWelcomeMessage && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-lg flex items-center animate-fadeIn">
          <CheckCircle className="h-5 w-5 mr-2" />
          <span>Welcome to Meet&Eat! You've successfully logged in.</span>
          <button 
            onClick={() => setShowWelcomeMessage(false)}
            className="ml-4 text-green-700 hover:text-green-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header with Navigation */}
      <div className="bg-white shadow-sm py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Beer className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-gray-900">Meet&Eat</span>
            </div>
            
            <div className="flex items-center space-x-2">
              {user ? (
                <div className="flex items-center space-x-4">
                  <Link 
                    to="/chat" 
                    className="p-2 text-gray-600 hover:text-orange-500 transition"
                  >
                    <MessageCircle className="h-6 w-6" />
                  </Link>
                  <Link 
                    to="/profile" 
                    className="px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition flex items-center space-x-2"
                  >
                    <User className="h-5 w-5" />
                    <span>{t('nav.profile')}</span>
                    <PremiumBadge className="ml-1" showLabel={false} />
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="p-2 rounded-xl text-gray-700 hover:text-orange-500 focus:outline-none"
                  >
                    <LogOut className="h-6 w-6" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Link 
                    to="/login" 
                    className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition"
                  >
                    <LogIn className="h-5 w-5 inline mr-2" />
                    <span>{t('nav.signIn')}</span>
                  </Link>
                  <Link 
                    to="/register" 
                    className="px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition"
                  >
                    <User className="h-5 w-5 inline mr-2" />
                    <span>{t('nav.signUp')}</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons - Mobile Optimized */}
      {!isMobile && renderHeroNavigation()}

      {/* Hero Section */}
      <div className="relative -mt-4">
        <div className="absolute inset-0 bg-black/50 z-10"></div>
        <div 
          className="h-[500px] md:h-[500px] bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80')"
          }}
        ></div>
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <div className="text-center text-white space-y-6 max-w-3xl px-4">
            <h1 className="text-4xl md:text-5xl font-bold">{t('home.hero.title')}</h1>
            <p className="text-lg md:text-xl">{t('home.hero.subtitle')}</p>
            <Link 
              to="/nearby"
              className="inline-block bg-orange-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-orange-600 transition"
            >
              {t('home.hero.cta')}
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 -mt-16 relative z-30">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 text-center">
            <div className="space-y-2">
              <Users className="h-8 w-8 text-orange-500 mx-auto" />
              <div className="text-3xl md:text-4xl font-bold text-gray-900">
                <CountUpNumber end={12547} duration={2000} />
              </div>
              <p className="text-gray-600">{t('home.stats.weeklyUsers')}</p>
            </div>
            <div className="space-y-2">
              <ChefHat className="h-8 w-8 text-orange-500 mx-auto" />
              <div className="text-3xl md:text-4xl font-bold text-gray-900">
                <CountUpNumber end={856} duration={2000} />
              </div>
              <p className="text-gray-600">{t('home.stats.weeklyRestaurants')}</p>
            </div>
            <div className="space-y-2">
              <Calendar className="h-8 w-8 text-orange-500 mx-auto" />
              <div className="text-3xl md:text-4xl font-bold text-gray-900">
                <CountUpNumber end={3289} duration={2000} />
              </div>
              <p className="text-gray-600">{t('home.stats.weeklyDinners')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('home.features.title')}</h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            {t('home.features.subtitle')}
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          <FeatureCard 
            icon={<Search className="h-8 w-8" />}
            title={t('home.features.search.title')}
            description={t('home.features.search.description')}
            delay={0}
          />
          <FeatureCard 
            icon={<Users className="h-8 w-8" />}
            title={t('home.features.meet.title')}
            description={t('home.features.meet.description')}
            delay={200}
          />
          <FeatureCard 
            icon={<Utensils className="h-8 w-8" />}
            title={t('home.features.share.title')}
            description={t('home.features.share.description')}
            delay={400}
          />
        </div>
      </div>

      {/* Philosophy Link */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 md:p-8 transform transition-all duration-500 hover:scale-105">
            <BookOpen className="h-12 w-12 text-white mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('home.philosophy.title')}</h2>
            <p className="text-white/90 text-base md:text-lg mb-6 max-w-2xl mx-auto">
              {t('home.philosophy.description')}
            </p>
            <Link
              to="/philosophy"
              className="inline-block bg-white text-orange-500 px-6 py-3 rounded-full font-semibold hover:bg-orange-50 transition"
            >
              {t('home.philosophy.cta')}
            </Link>
          </div>
        </div>
      </div>

      {/* Additional Stats Section */}
      <div className="bg-orange-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('home.impact.title')}</h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.impact.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            <StatCard 
              icon={<Heart className="h-8 w-8" />}
              value={1250}
              label={t('home.impact.connections')}
              duration={2500}
              prefix="+"
            />
            <StatCard 
              icon={<Star className="h-8 w-8" />}
              value={4.8}
              label={t('home.impact.rating')}
              duration={1500}
              suffix="/5"
            />
            <StatCard 
              icon={<MapPin className="h-8 w-8" />}
              value={42}
              label={t('home.impact.cities')}
              duration={2000}
            />
            <StatCard 
              icon={<Clock className="h-8 w-8" />}
              value={95}
              label={t('home.impact.onTime')}
              duration={2200}
              suffix="%"
            />
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('home.howItWorks.title')}</h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            {t('home.howItWorks.subtitle')}
          </p>
        </div>
        
        <div className="relative">
          {/* Connection Line - Hidden on mobile, visible on md+ */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-orange-200 -translate-y-1/2 z-0"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 text-center">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">1</div>
              <h3 className="text-xl font-semibold mb-4">{t('home.howItWorks.step1.title')}</h3>
              <p className="text-gray-600">{t('home.howItWorks.step1.description')}</p>
            </div>
            
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 text-center">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">2</div>
              <h3 className="text-xl font-semibold mb-4">{t('home.howItWorks.step2.title')}</h3>
              <p className="text-gray-600">{t('home.howItWorks.step2.description')}</p>
            </div>
            
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 text-center">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">3</div>
              <h3 className="text-xl font-semibold mb-4">{t('home.howItWorks.step3.title')}</h3>
              <p className="text-gray-600">{t('home.howItWorks.step3.description')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Popular Categories */}
      <div className="bg-gray-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('home.categories.title')}</h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.categories.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: <Heart className="h-8 w-8" />, name: t('home.categories.blindDates'), link: '/blind-dates', color: 'bg-pink-100 text-pink-600' },
              { icon: <Users className="h-8 w-8" />, name: t('home.categories.groups'), link: '/nearby-listings', color: 'bg-purple-100 text-purple-600' },
              { icon: <Award className="h-8 w-8" />, name: t('home.categories.fineDining'), link: '/nearby', color: 'bg-red-100 text-red-600' },
            ].map((category, index) => (
              <Link 
                key={index}
                to={category.link}
                className="bg-white rounded-xl shadow-md p-4 md:p-6 text-center transform transition-transform hover:scale-105"
              >
                <div className={`w-12 h-12 md:w-16 md:h-16 ${category.color} rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4`}>
                  {category.icon}
                </div>
                <h3 className="font-medium text-gray-900 text-sm md:text-base">{category.name}</h3>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('home.testimonials.title')}</h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.testimonials.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <TestimonialCard
              name={t('home.testimonials.sarah.name')}
              image="https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=150&h=150&auto=format&fit=crop"
              text={t('home.testimonials.sarah.text')}
              rating={5}
              location={t('home.testimonials.sarah.location')}
              index={0}
            />
            <TestimonialCard
              name={t('home.testimonials.michael.name')}
              image="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=150&h=150&auto=format&fit=crop"
              text={t('home.testimonials.michael.text')}
              rating={5}
              location={t('home.testimonials.michael.location')}
              index={1}
            />
            <TestimonialCard
              name={t('home.testimonials.emma.name')}
              image="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=150&h=150&auto=format&fit=crop"
              text={t('home.testimonials.emma.text')}
              rating={5}
              location={t('home.testimonials.emma.location')}
              index={2}
            />
          </div>
        </div>
      </div>

      {/* Growth Stats */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 py-16 md:py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">{t('home.growth.title')}</h2>
            <p className="text-lg md:text-xl opacity-90 max-w-3xl mx-auto">
              {t('home.growth.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 md:p-8 text-center">
              <TrendingUp className="h-10 w-10 mx-auto mb-4" />
              <div className="text-3xl md:text-4xl font-bold mb-2">
                <CountUpNumber end={127} duration={2000} />%
              </div>
              <p className="opacity-90">{t('home.growth.userGrowth')}</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 md:p-8 text-center">
              <MessageCircle className="h-10 w-10 mx-auto mb-4" />
              <div className="text-3xl md:text-4xl font-bold mb-2">
                <CountUpNumber end={98} duration={2000} />%
              </div>
              <p className="opacity-90">{t('home.growth.satisfactionRate')}</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 md:p-8 text-center">
              <Zap className="h-10 w-10 mx-auto mb-4" />
              <div className="text-3xl md:text-4xl font-bold mb-2">
                <CountUpNumber end={24} duration={2000} />
              </div>
              <p className="opacity-90">{t('home.growth.newCities')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('home.cta.title')}</h2>
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {t('home.cta.subtitle')}
          </p>
          <Link
            to="/register"
            className="inline-block bg-orange-500 text-white px-6 py-3 md:px-8 md:py-4 rounded-full font-semibold hover:bg-orange-600 transition text-base md:text-lg"
          >
            {t('home.cta.button')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home;